<template>
  <figure
    class="dato-asset"
    ref="figureElement"
    :style="{
      '--background-color': getBackgroundColor,
      '--scale': scale,
      '--aspect-ratio': aspectRatio
    }"
    :class="{ '-fade-in': fadeIn !== 'none', '-in-viewport': isElementVisible }"
  >
    <Image
      :alt="asset.alt ?? ''"
      class="responsiveimage image"
      v-if="image.responsiveImage"
      :data="image.responsiveImage as ResponsiveImageType"
      :lazy-load="lazyLoad"
      :aspectRatio="aspectRatio"
      ref="imageElement"
    />
    <video v-lazy-load 
      v-else-if="asset.video"
      class="video"
      preload="none"
      playsinline
      :data-src="asset.video.mp4Med"
      muted
      autoplay
      loop
    />
    <img v-lazy-load  class="image" v-else :data-src="image.url" :alt="image.alt ?? ''" />
    <figcaption class="caption" v-if="caption">{{ caption }}</figcaption>
    <footer
      v-show="!hideCopyright"
      v-if="copyright"
      :class="{ '-open': isCopyrightOpen }"
      @click="toggleCopyright()"
      class="footer"
      :style="{ top: `${copyrightPositionTop}px` }"
    >
      <small class="copyright">
        <span class="inner">{{ copyright }}</span>
      </small>
      <button class="toggle" type="button" aria-label="Toggle"></button>
    </footer>
  </figure>
</template>

<script setup lang="ts">
import useIntersectionObserver from '~/utils/useIntersectionObserver';
import useImageScaleForHover from '~/utils/useImageScaleForHover';
import { Image, type ResponsiveImageType } from 'vue-datocms';
import type { DatoAssetDto } from '~/dato/dato';
import { useCopyright } from '~/utils/useCopyright';

const props = defineProps<{
  caption?: string;
  fadeIn?: 'immediate' | 'on-intersection' | 'none';
  backgroundColor?: string;
  asset: DatoAssetDto;
  assetVs?: DatoAssetDto;
  aspectRatio?: string;
  lazyLoad?: boolean;
  hideCopyright?: boolean;
}>();

const fadeIn = props.fadeIn ?? 'none';
const figureElement = ref<HTMLElement | null>(null);
const imageElement = ref<HTMLElement | null>(null);
const viewport = ref<'vs' | 'vl'>('vl');

const { isElementVisible, start } = useIntersectionObserver(figureElement);
const { scale } = useImageScaleForHover(figureElement);
const {
  toggle: toggleCopyright,
  isOpen: isCopyrightOpen,
  top: copyrightPositionTop
} = useCopyright(imageElement);

if (fadeIn === 'none') {
  isElementVisible.value = true;
}

const getBackgroundColor = computed(function () {
  if (props.asset?.customData?.Color) {
    return props.asset.customData.Color;
  }

  if (props.asset?.colors?.length && props.asset.colors[0].hex) {
    return props.asset.colors[0]?.hex;
  }

  return props.backgroundColor ?? '#e6e6e6';
});

const copyright = computed(function () {
  return props.asset?.copyright ?? null;
});

const runtimeConfig = useRuntimeConfig();
const { width } = useWindowSize();

const image = computed(() => {
  if (width.value < runtimeConfig.public.mobileBreakpoint && props.assetVs) {
    viewport.value = 'vs';

    return props.assetVs;
  }
  viewport.value = 'vl';

  return props.asset;
});

onMounted(function () {
  // add animation
  if (fadeIn === 'on-intersection') {
    start();
  } else if (fadeIn === 'immediate') {
    isElementVisible.value = true;
  }
});
</script>

<style scoped lang="scss">
.dato-asset {
  --scale: 1;

  position: relative;
  // width: var(--width, 100%);
  // height: var(--height, 100%);

  @media (--vs) {
    --transition-delay: 0 !important;
  }

  @media (--vs) {
    --transition-delay: 150ms;
  }

  &.-hover-enabled {
    transition: clip-path 500ms cubic-bezier(0.4, 0, 0.2, 1);
    scale: var(--scale);
    clip-path: inset(5px 5px 5px 5px);

    @media (hover: hover) {
      &:hover {
        clip-path: inset(0% 0% 0% 0%);

        > .caption {
          transform: translateY(-5px);
        }
      }
    }
  }

  &.-fade-in {
    &.-in-viewport {
      &::after {
        clip-path: inset(0% 0% 100% 0%);
      }
    }

    &:not(.-in-viewport) {
      &::after {
        clip-path: inset(0% 0% 0% 0%);
      }
    }

    &::after {
      content: '';
      background-color: color-mix(in srgb, var(--background-color) 20%, white);
      position: absolute;
      inset: -1px;
      width: calc(100% + 2px);
      height: calc(100% + 2px);
      transition: clip-path 800ms cubic-bezier(0.4, 0, 0.2, 1);
      transition-delay: var(--transition-delay);
    }
  }

  > .placeholder,
  > .responsiveimage,
  > .image,
  > .video {
    width: 100%;
    height: 100%;
    aspect-ratio: var(--aspect-ratio);
  }

  > .responsiveimage {
    width: 100%;
    height: 100%;
    max-width: unset !important;

    :deep(> img) {
      width: 100%;
      height: 100%;
    }

    :deep(picture) {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      img {
        object-fit: cover;
      }
    }
  }

  > .placeholder {
    background-color: var(--color-grey);
  }

  > .video {
    width: 100%;
    height: 100%;
  }

  > .caption {
    @include text-sm(700);

    padding: 1rem 0 0 0;
    display: block;
    transition: transform 500ms;
  }

  > .footer {
    position: absolute;
    right: 1rem;
    top: 0;
    transform: translateY(calc(-100% - 1rem));
    z-index: 10;
    display: flex;
    justify-content: flex-end;

    &.-open,
    &:hover {
      > .copyright {
        width: 100%;
        background-color: var(--color-black);

        > .inner {
          transform: translate(0, -1px);
          opacity: 1;
          transition-delay: var(--delay);
          transition-duration: var(--duration);
        }
      }

      > .toggle {
        opacity: 1;
      }
    }

    > .copyright {
      background-color: transparent;
      color: var(--color-white);
      border-radius: 1.5rem;
      padding: 0 3rem 0 1.25rem;
      height: 2.3125rem;
      display: flex;
      align-items: center;
      //clip-path: inset(0% 0% 0% 100%);
      width: 1.5rem;
      transition:
        width 500ms 0ms,
        background-color 500ms 0ms;
      overflow: clip;
      transform-origin: right center;

      > .inner {
        --duration: 300ms;
        --delay: 600ms;

        @include text-xs(300);

        transform: translate(1rem, -1px);
        opacity: 0;
        transition:
          transform 0ms,
          opacity 0ms;
      }
    }

    > .toggle {
      width: 1.5625rem;
      height: 1.5625rem;
      border-radius: 1.81163rem;
      outline: none;
      border: none;
      background-image: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='25' height='25' rx='12.5' fill='white'/%3E%3Cpath d='M12.6763 7.37157C11.8974 7.37157 11.1716 7.53089 10.499 7.81412C9.82629 8.09735 9.25983 8.50449 8.76418 9.00015C8.26852 9.4958 7.89679 10.08 7.61356 10.7526C7.33033 11.4253 7.18871 12.1334 7.18871 12.8769C7.18871 13.6557 7.33033 14.3638 7.61356 15.0365C7.89679 15.7092 8.26852 16.2933 8.76418 16.789C9.25983 17.3023 9.82629 17.7095 10.499 17.9927C11.1716 18.2759 11.8974 18.4175 12.6763 18.4175C13.4552 18.4175 14.1632 18.2759 14.8359 17.9927C15.5086 17.7095 16.075 17.3023 16.5707 16.789C17.0664 16.2933 17.4558 15.6915 17.739 15.0188C18.0223 14.3461 18.1639 13.6203 18.1639 12.8415C18.1639 12.098 18.0223 11.4076 17.739 10.7349C17.4558 10.08 17.0664 9.4958 16.5707 9.00015C16.075 8.50449 15.5086 8.09735 14.8359 7.81412C14.1632 7.53089 13.4552 7.37157 12.6763 7.37157ZM12.6763 6.6635C13.526 6.6635 14.3403 6.82282 15.1014 7.14145C15.8449 7.46008 16.5176 7.90263 17.0841 8.46909C17.6505 9.03555 18.0931 9.69052 18.4117 10.434C18.7303 11.1952 18.9073 12.0095 18.9073 12.8769C18.9073 13.7619 18.7303 14.5762 18.4117 15.3197C18.0931 16.0809 17.6505 16.7359 17.0841 17.3023C16.5176 17.8688 15.8449 18.329 15.1014 18.6477C14.3403 18.9663 13.526 19.1256 12.6763 19.1256C11.8089 19.1256 10.9946 18.9663 10.2511 18.6477C9.48995 18.329 8.83498 17.8688 8.26852 17.3023C7.70207 16.7359 7.24182 16.0809 6.92318 15.302C6.60455 14.5408 6.44523 13.7265 6.44523 12.8415C6.44523 11.9918 6.60455 11.1775 6.92318 10.434C7.24182 9.69052 7.70207 9.03555 8.26852 8.46909C8.83498 7.90263 9.48995 7.46008 10.2511 7.14145C10.9946 6.82282 11.8089 6.6635 12.6763 6.6635ZM14.6412 10.3278C14.2341 10.1331 13.7384 10.0269 13.1719 10.0269C12.7648 10.0269 12.3931 10.0977 12.0567 10.2393C11.7204 10.3809 11.4372 10.5579 11.1893 10.8057C10.9415 11.0536 10.7468 11.3368 10.6052 11.6554C10.4636 11.9918 10.4105 12.3635 10.4105 12.7529C10.4105 13.1955 10.4813 13.5849 10.6229 13.939C10.7645 14.293 10.9592 14.5762 11.207 14.8241C11.4549 15.0719 11.7558 15.2666 12.1098 15.3905C12.4462 15.5321 12.8179 15.5852 13.2428 15.5852C13.4729 15.5852 13.703 15.5675 13.9508 15.5321C14.1987 15.4967 14.4465 15.4082 14.6943 15.2843L14.7474 16.134C14.3226 16.2933 13.8092 16.3641 13.2428 16.3641C12.6763 16.3641 12.1806 16.2933 11.7204 16.1163C11.2601 15.957 10.853 15.7269 10.5344 15.4082C10.198 15.1073 9.9325 14.7356 9.75548 14.2753C9.56076 13.8328 9.47225 13.3194 9.47225 12.7529C9.47225 12.2396 9.56076 11.7616 9.75548 11.3368C9.9325 10.9119 10.198 10.5402 10.5344 10.2216C10.853 9.90294 11.2424 9.67282 11.7027 9.4958C12.1452 9.31878 12.6409 9.23027 13.1719 9.23027C13.7738 9.23027 14.2872 9.31878 14.6943 9.4781L14.6412 10.3278Z' fill='black'/%3E%3C/svg%3E%0A");
      background-size: cover;
      position: absolute;
      inset: 0.38rem 0.38rem 0.38rem auto;
      cursor: pointer;
      opacity: 0.8;
      transition: opacity 500ms 0ms;
    }
  }
}
</style>
